import { Injectable } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { UserActivityTypeDto } from '@treasury/api/channel';
import { CompanyViewModel } from '@treasury/domain-components/view-models/company.viewModel';
import { UsersService } from '@treasury/domain/backoffice/services';
import { CompanyUserService, CompanyUserSummary } from '@treasury/domain/company/user';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { html } from 'lit';
import { RowActionCommand, RowActionCommandResult } from '../base.cmd';
import { CompanyUserActionType } from './user-actions.types';

@Injectable()
export class ResendEnrollmentLinkCommand implements RowActionCommand<
    CompanyUserSummary,
    CompanyUserActionType
> {
    constructor(
        private readonly dialogService: TmDialogService,
        private readonly userService: UsersService,
        private readonly companyUserService: CompanyUserService,
        private readonly toastService: TmToastService
    ) {}

    public command(
        company: CompanyViewModel,
        isUisEnabled: boolean
    ): RowActionCommandResult<CompanyUserSummary, CompanyUserActionType> {
        return {
            type: CompanyUserActionType.ResendEnrollmentLink,
            title: 'Resend Enrollment Link',
            hiddenCondition: (user: CompanyUserSummary) =>
                !isUisEnabled || !company.active || !user.canResendUisEnrollmentLink,
            action: async (user: CompanyUserSummary) => this.execute(user, company),
        };
    }

    private async execute(user: CompanyUserSummary, company: CompanyViewModel) {
        const { reason } = await this.dialogService.open(
            html`<div>
                ${`An email will be sent to ${user.userName} with a link to start the enrollment process.`}
            </div>`,
            'Resend Enrollment Email',
            {
                buttons: [
                    {
                        label: 'Resend Enrollment Email',
                        reason: DynamicComponentExitReason.Confirm,
                        posture: TmDialogButtonPosture.Primary,
                    },
                    {
                        label: 'Cancel',
                        reason: DynamicComponentExitReason.Cancel,
                        posture: TmDialogButtonPosture.Secondary,
                    },
                ],
            }
        );

        if (reason !== DynamicComponentExitReason.Confirm) {
            return;
        }
        try {
            if (!company.id || !user.id) {
                throw new Error('Company ID and User ID are required');
            }
            await this.companyUserService.sendEnrollmentEmail(company.id, user.id);
            await this.userService.saveUserActivity(
                UserActivityTypeDto.Admin,
                'Resend Enrollment Email'
            );
            this.toastService.show(
                `Enrollment email was successfully sent to user ${user.userName}.`,
                TmToastPosture.Success
            );
        } catch {
            this.toastService.show(
                `Enrollment email for user ${user.userName} was unable to be sent.`,
                TmToastPosture.Danger
            );
        }
    }
}
